// File utilities for AudioRecorder
import { FILE_EXTENSIONS, MAX_AUDIO_UPLOAD_BYTES } from "../Constants";

/** True if the blob decodes with Web Audio API and has meaningful duration (seconds). */
export const isAudioBlobPlayable = async (blob) => {
  if (!blob || blob.size < 64) return false;
  return new Promise((resolve) => {
    try {
      const fileReader = new FileReader();
      fileReader.onloadend = () => {
        try {
          const arrayBuffer = fileReader.result;
          if (!arrayBuffer || !arrayBuffer.byteLength) {
            resolve(false);
            return;
          }
          const audioContext = new (window.AudioContext ||
            window.webkitAudioContext)();
          const copy = arrayBuffer.slice(0);
          audioContext.decodeAudioData(
            copy,
            (audioBuffer) => {
              const ok =
                !!audioBuffer &&
                Number.isFinite(audioBuffer.duration) &&
                audioBuffer.duration > 0.05;
              audioContext.close();
              resolve(ok);
            },
            () => {
              audioContext.close();
              resolve(false);
            },
          );
        } catch {
          resolve(false);
        }
      };
      fileReader.onerror = () => resolve(false);
      fileReader.readAsArrayBuffer(blob);
    } catch {
      resolve(false);
    }
  });
};

/** Fetch URL and check playability (same decode path as in-app duration checks). */
export const isAudioUrlPlayable = async (url) => {
  if (!url) return false;
  try {
    const response = await fetch(url);
    if (!response.ok) return false;
    const blob = await response.blob();
    return isAudioBlobPlayable(blob);
  } catch {
    return false;
  }
};

// Calculate audio duration from blob
export const calculateAudioDuration = async (blob) => {
  return new Promise((resolve) => {
    try {
      const fileReader = new FileReader();
      fileReader.onloadend = () => {
        try {
          const arrayBuffer = fileReader.result;
          const audioContext = new (window.AudioContext ||
            window.webkitAudioContext)();

          audioContext.decodeAudioData(
            arrayBuffer,
            (audioBuffer) => {
              const duration = audioBuffer.duration;
              resolve(duration);
              audioContext.close();
            },
            (error) => {
              console.warn(
                "Could not decode audio for duration calculation:",
                error
              );
              resolve(0); // Return 0 duration instead of rejecting
              audioContext.close();
            }
          );
        } catch (error) {
          console.warn("Error in audio duration calculation:", error);
          resolve(0); // Return 0 duration instead of rejecting
        }
      };

      fileReader.onerror = (error) => {
        console.warn("FileReader error for duration calculation:", error);
        resolve(0); // Return 0 duration instead of rejecting
      };

      fileReader.readAsArrayBuffer(blob);
    } catch (error) {
      console.warn("Error setting up audio duration calculation:", error);
      resolve(0); // Return 0 duration instead of rejecting
    }
  });
};

// Get file extension from MIME type
export const getFileExtension = (mimeType) => {
  if (!mimeType) return "webm"; // default

  for (const [type, extension] of Object.entries(FILE_EXTENSIONS)) {
    if (mimeType.includes(type.replace("audio/", ""))) {
      return extension;
    }
  }

  // If no match, try to extract from mime type
  const parts = mimeType.split("/");
  if (parts.length > 1) {
    return parts[1];
  }

  return "webm"; // fallback
};

// Sanitize filename
export const sanitizeFilename = (filename) => {
  if (!filename) return "audio";
  return filename.replace(/[^a-zA-Z0-9]/g, "_");
};

// Validate audio file size (max from shared constant, currently 2 GiB)
export const validateAudioFile = (file) => {
  if (file.size > MAX_AUDIO_UPLOAD_BYTES) {
    throw new Error(
      `Audio file size exceeds limit (max ${Math.floor(MAX_AUDIO_UPLOAD_BYTES / (1024 * 1024 * 1024))} GB)`,
    );
  }

  // Check if it's an audio file
  if (!file.type.startsWith("audio/") && !isAudioFile(file)) {
    throw new Error("Invalid file type. Please select an audio file.");
  }

  return true;
};

// Check if file is audio based on extension or MIME type
export const isAudioFile = (file) => {
  const audioExtensions = [
    "mp3",
    "wav",
    "ogg",
    "m4a",
    "aac",
    "mp4",
    "webm",
    "mpeg",
    "mpg",
    "m4b",
    "mov",
    "3gp",
    "amr",
    "flac",
    "caf",
    "3gpp",
    "wma",
  ];

  const filename = file.name || "";
  const extension = filename.split(".").pop()?.toLowerCase();

  return (
    file.type.startsWith("audio/") ||
    (extension && audioExtensions.includes(extension))
  );
};

// Create download links for audio and metadata
export const createDownloadLinks = (
  audioBlob,
  audioFilename,
  metadata,
  prompts
) => {
  // Create audio download link
  const audioDownloadLink = document.createElement("a");
  audioDownloadLink.href = URL.createObjectURL(audioBlob);
  audioDownloadLink.setAttribute("download", audioFilename);
  audioDownloadLink.setAttribute("target", "_blank");
  audioDownloadLink.click();

  // Create metadata text file
  const textContent = prompts
    .map(([prompt]) => `${prompt[0]}: ${metadata[prompt[0]] || ""}`)
    .join("\n");
  const textBlob = new Blob([textContent], { type: "text/plain" });

  const textDownloadLink = document.createElement("a");
  const textFilename = `audio_info_${Date.now()}.txt`;
  const textURL = URL.createObjectURL(textBlob);
  textDownloadLink.href = textURL;
  textDownloadLink.setAttribute("download", textFilename);
  textDownloadLink.setAttribute("target", "_blank");
  textDownloadLink.click();

  // Cleanup URLs
  setTimeout(() => {
    URL.revokeObjectURL(audioDownloadLink.href);
    URL.revokeObjectURL(textURL);
  }, 1000);
};

// Clean and validate SOAP notes
export const cleanSoapNotes = (soapNotes) => {
  if (!soapNotes) return null;

  console.log("Cleaning SOAP notes:", soapNotes);

  const cleaned = {};

  // Clean transcript_only
  if (soapNotes.transcript_only) {
    try {
      // Check if it's JSON schema (contains "properties" and "type")
      if (
        typeof soapNotes.transcript_only === "string" &&
        soapNotes.transcript_only.includes('"properties"') &&
        soapNotes.transcript_only.includes('"type"')
      ) {
        console.log("Detected JSON schema in transcript_only, skipping");
        cleaned.transcript_only = null;
      } else {
        // Try to parse JSON and extract clean text
        let cleanedText = soapNotes.transcript_only;
        try {
          const parsed = JSON.parse(soapNotes.transcript_only);
          if (parsed.rewritten_section) {
            cleanedText = parsed.rewritten_section;
          } else if (typeof parsed === "string") {
            cleanedText = parsed;
          }
        } catch (parseError) {
          // If it's not JSON, use as-is
          cleanedText = soapNotes.transcript_only;
        }
        cleaned.transcript_only = cleanedText;
        console.log("transcript_only cleaned successfully");
      }
    } catch (error) {
      console.error("Error cleaning transcript_only:", error);
      cleaned.transcript_only = null;
    }
  }

  // Clean facts_only
  if (soapNotes.facts_only) {
    try {
      // Check if it's JSON schema (contains "properties" and "type")
      if (
        typeof soapNotes.facts_only === "string" &&
        soapNotes.facts_only.includes('"properties"') &&
        soapNotes.facts_only.includes('"type"')
      ) {
        console.log("Detected JSON schema in facts_only, skipping");
        cleaned.facts_only = null;
      } else {
        // Try to parse JSON and extract clean text
        let cleanedText = soapNotes.facts_only;
        try {
          const parsed = JSON.parse(soapNotes.facts_only);
          if (parsed.rewritten_section) {
            cleanedText = parsed.rewritten_section;
          } else if (typeof parsed === "string") {
            cleanedText = parsed;
          }
        } catch (parseError) {
          // If it's not JSON, use as-is
          cleanedText = soapNotes.facts_only;
        }
        cleaned.facts_only = cleanedText;
        console.log("facts_only cleaned successfully");
      }
    } catch (error) {
      console.error("Error cleaning facts_only:", error);
      cleaned.facts_only = null;
    }
  }

  console.log("Cleaned SOAP notes result:", cleaned);
  return cleaned;
};

// Generate PDF from transcription results
export const generatePDF = (
  transcriptionData,
  userInfo,
  prompts,
  recordingDuration
) => {
  const { jsPDF } = require("jspdf");
  const doc = new jsPDF();

  // Add title
  doc.setFontSize(20);
  doc.text("Medical Report Transcription", 20, 20);

  // Add patient info
  doc.setFontSize(14);
  doc.text(`Patient: ${userInfo[prompts[0][0]] || "Unknown"}`, 20, 35);
  doc.text(`Date: ${new Date().toLocaleDateString()}`, 20, 45);
  doc.text(
    `Duration: ${Math.floor(recordingDuration.minutes)}:${String(
      recordingDuration.seconds % 60
    ).padStart(2, "0")}`,
    20,
    55
  );

  // Add transcript
  doc.setFontSize(16);
  doc.text("Transcript:", 20, 75);
  doc.setFontSize(10);
  let yPos = 85;
  transcriptionData.transcript?.forEach((line) => {
    if (yPos > 280) {
      doc.addPage();
      yPos = 20;
    }
    // Split long lines to fit page width
    const lines = doc.splitTextToSize(line, 170);
    lines.forEach((splitLine) => {
      if (yPos > 280) {
        doc.addPage();
        yPos = 20;
      }
      doc.text(splitLine, 20, yPos);
      yPos += 6;
    });
  });

  // Add facts
  doc.addPage();
  doc.setFontSize(16);
  doc.text("Extracted Facts:", 20, 20);
  doc.setFontSize(10);
  yPos = 30;
  transcriptionData.facts?.forEach((fact) => {
    if (yPos > 280) {
      doc.addPage();
      yPos = 20;
    }
    doc.text(fact.text, 20, yPos);
    doc.text(`Category: ${fact.group}`, 20, yPos + 5);
    yPos += 15;
  });

  // Add SOAP notes
  if (transcriptionData.soapNotes) {
    doc.addPage();
    doc.setFontSize(16);
    doc.text("SOAP Notes:", 20, 20);
    doc.setFontSize(10);
    yPos = 30;

    if (transcriptionData.soapNotes.transcript_only) {
      doc.text("Transcript-Based SOAP Notes:", 20, yPos);
      yPos += 10;
      const soapLines = doc.splitTextToSize(
        transcriptionData.soapNotes.transcript_only,
        170
      );
      soapLines.forEach((line) => {
        if (yPos > 280) {
          doc.addPage();
          yPos = 20;
        }
        doc.text(line, 20, yPos);
        yPos += 6;
      });
    }

    if (transcriptionData.soapNotes.facts_only) {
      doc.addPage();
      doc.text("Facts-Based SOAP Notes:", 20, yPos);
      yPos += 10;
      const soapLines = doc.splitTextToSize(
        transcriptionData.soapNotes.facts_only,
        170
      );
      soapLines.forEach((line) => {
        if (yPos > 280) {
          doc.addPage();
          yPos = 20;
        }
        doc.text(line, 20, yPos);
        yPos += 6;
      });
    }
  }

  // Save the PDF
  const patientNameForPDF = userInfo[prompts[0][0]] || "Unknown";
  doc.save(`transcription_${patientNameForPDF}_${Date.now()}.pdf`);
};
